<template>
  <div class="saved-views-view">
    <div class="container">
      <span class="back-to-playground" @click="backClicked">
        <arrow-svg class="arrow-icon"></arrow-svg> Back</span
      >
      <h1 class="page-title">
        My views
        <tooltip
          :content="
            'Please note, storing your data is offered as a service. <br />We are not responsible for data loss of any kind, like - but not limited to - server crash, tool’s functionality update or addition, phase-out of older products.'
          "
        />
      </h1>

      <table class="simple-table" v-if="sortedViewData.length">
        <thead>
          <th
            @click="changeSort('region')"
            ref="regionColumn"
            :style="getStyle('region')"
          >
            Region
            <up-svg
              class="up-icon"
              v-if="getSort('region') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="getSort('region') === 'asc'"
            ></down-svg>
          </th>
          <th
            @click="changeSort('description')"
            ref="descriptionColumn"
            :style="getStyle('description')"
          >
            Description
            <up-svg
              class="up-icon"
              v-if="getSort('description') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="getSort('description') === 'asc'"
            ></down-svg>
          </th>
          <th
            @click="changeSort('createdOn')"
            ref="createdOnColumn"
            :style="getStyle('createdOn')"
          >
            Date created
            <up-svg
              class="up-icon"
              v-if="getSort('createdOn') === 'desc'"
            ></up-svg>
            <down-svg
              class="down-icon"
              v-if="getSort('createdOn') === 'asc'"
            ></down-svg>
          </th>
          <th></th>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in sortedViewData"
            :key="index"
            @mouseover="hover = row.name"
            @mouseleave="hover = ''"
            @click="onApplyView(row)"
            :class="{ disabled: row.region !== region }"
          >
            <td>
              {{ row.region }}
            </td>
            <td>
              {{ row.name }}
            </td>
            <td>{{ formatDate(row.createdOn, "yyyy-MM-dd") || "-" }}</td>
            <td style="text-align: center; width: 50px;">
              <transition name="slide-fade" mode="out-in">
                <a
                  href="#"
                  class="link-active link"
                  v-if="hover === row.name"
                  @click.stop="onDeleteClicked(row)"
                  ><delete-svg class="delete-icon"></delete-svg
                ></a>
              </transition>
            </td>
          </tr>
        </tbody>
      </table>

      <p class="meta-info" v-if="!sortedViewData.length && !loading">
        No solutions have been saved yet.
      </p>
      <p class="meta-info" v-if="loading">Loading...</p>
    </div>
  </div>
</template>

<script>
import { http } from "@/services/http";
import { EventBus } from "@/eventbus.js";
import { format } from "date-fns";
import DeleteViewModal from "@/components/modals/DeleteViewModal";
import deleteSvg from "@/assets/svg/icons/signify-icons_16-delete-16px.svg?inline";
import arrowSvg from "@/assets/svg/icons/signify-icons_13-arrow-button-16px.svg?inline";
import downSvg from "@/assets/svg/icons/signify-icons_04-down-12px.svg?inline";
import upSvg from "@/assets/svg/icons/signify-icons_11-up-12px.svg?inline";
import { mapState } from "vuex";
import { cloneDeep } from "lodash";

export default {
  name: "SavedViews",
  modals: { DeleteViewModal },
  data() {
    return {
      viewData: [],
      hover: "",
      loading: true,
      sorting: null,
      columnWidths: null
    };
  },
  components: {
    deleteSvg,
    arrowSvg,
    downSvg,
    upSvg
  },
  computed: {
    ...mapState("brain", ["region"]),
    sortedViewData() {
      if (!this.viewData) return [];
      if (!this.sorting) return this.viewData;

      const ascending = cloneDeep(this.viewData).sort((a, b) => {
        return a[this.sorting.property] > b[this.sorting.property] ? 1 : -1;
      });

      return this.sorting.order === "asc" ? ascending : ascending.reverse();
    }
  },
  watch: {
    sortedViewData() {
      this.$nextTick(() => {
        this.columnWidths = {
          region: this.$refs.regionColumn.clientWidth,
          description: this.$refs.descriptionColumn.clientWidth,
          createdOn: this.$refs.createdOnColumn.clientWidth
        };
      });
    }
  },
  created() {
    this.$store.commit("brain/ui/showControls", false);
  },
  mounted() {
    this.getSavedViews();
    EventBus.$on("viewSaved", () => {
      this.getSavedViews();
    });
  },
  methods: {
    formatDate(value) {
      return format(value, "yyyy-MM-dd");
    },

    getSavedViews() {
      this.loading = false;
      http.get(`/api/v2/users/profile/settings/views/all`).then(response => {
        const viewData = response.data.data.filter(
          item => item.name !== "AUTOSAVE"
        ); //don't show the view we reserved for autosave functionality
        this.viewData = this.sortViewsByRegion(viewData);
        this.loading = false;
      });
    },

    onDeleteClicked(row) {
      this.$modalRouter.push({
        name: "DeleteViewModal",
        props: { show: true, view: row },
        model: "show",
        on: { onDeleteView: this.deleteView }
      });
    },

    deleteView(view) {
      http({
        method: "delete",
        url: `/api/v2/users/profile/settings/views/remove?name=${view.name}`
      }).then(() => {
        this.getSavedViews();
      });
    },

    onApplyView(row) {
      if (row === null) return;

      const decodedDisplaySettings = JSON.parse(row.view);
      function onComplete() {
        this.$store.commit("brain/ui/setPaneSize", {
          size: decodedDisplaySettings.paneSize
        });
        this.$store.commit("brain/ui/minimizeInput", {
          type: "module",
          minimize: decodedDisplaySettings.minimizeModuleInput
        });
        this.$store.commit("brain/ui/minimizeInput", {
          type: "driver",
          minimize: decodedDisplaySettings.minimizeDriverInput
        });
        this.$store.dispatch("brain/ui/showModules", {
          show: decodedDisplaySettings.showModules
        });
        this.$store.dispatch("brain/ui/showDrivers", {
          show: decodedDisplaySettings.showDrivers
        });

        decodedDisplaySettings.savedModuleColumnVisibility.map(column => {
          let columnObject = { item: column, value: column.value };
          this.$store.commit("brain/ui/setModuleColumnVisibility", {
            value: columnObject
          });
        });

        decodedDisplaySettings.savedDriverColumnVisibility.map(column => {
          let columnObject = { item: column, value: column.value };
          this.$store.commit("brain/ui/setDriverColumnVisibility", {
            value: columnObject
          });
        });
      }
      this.$router.push(
        { name: "advanced", params: { fetchViewOnInit: false } },
        onComplete.bind(this)
      );
    },
    sortViewsByRegion(views) {
      const regionOrderedViews = views
        .filter(view => {
          return view.region === this.region;
        })
        .sort((a, b) => {
          return a.name > b.name ? 1 : -1;
        });

      views = views
        .filter(view => {
          return view.region !== this.region;
        })
        .sort((a, b) => {
          if (a.view.region > b.view.region) return 1;
          if (a.view.region < b.view.region) return -1;

          if (a.name > b.name) return 1;
          if (a.name < b.name) return -1;
        });

      views.map(view => {
        regionOrderedViews.push(view);
      });

      return regionOrderedViews;
    },
    backClicked() {
      this.$store.commit("brain/global/setCheckReload", true);
      this.$router.push({ name: "advanced" });
    },
    changeSort(property) {
      if (!this.sorting || this.sorting.property !== property) {
        this.sorting = {
          property: property,
          order: "desc"
        };
      } else {
        this.sorting.order = this.sorting.order === "desc" ? "asc" : "desc";
      }
    },
    getSort(property) {
      if (this.sorting && this.sorting.property === property) {
        return this.sorting.order;
      } else {
        return null;
      }
    },
    getStyle(property) {
      return this.columnWidths
        ? { width: `${this.columnWidths[property]}px` }
        : {};
    }
  },
  destroyed() {
    EventBus.$off("viewSaved");
  }
};
</script>

<style lang="scss" scoped>
.page-title {
  margin-bottom: 40px;
}

.back-to-playground {
  margin-top: 115px;
  margin-bottom: 5px;
  display: block;
  color: $blue-500;
  cursor: pointer;
}

thead {
  border-top: 1px $mid-gray-200 solid;
  background: #fff;

  th {
    padding: 16px 10px;
  }
}

tbody {
  background: #fff;
}

th {
  cursor: pointer;
  min-width: 100px;
}

tr {
  cursor: pointer;

  &.disabled {
    cursor: default;
    opacity: 0.5;
  }
}

.delete-icon {
  width: 20px;

  path {
    fill: $dark-gray-900;
  }
}

.arrow-icon {
  width: 16px;
  transform: rotate(180deg);

  path {
    fill: $blue-500;
  }
}

.up-icon,
.down-icon {
  width: 16px;
}

.saved-views-view {
  flex-grow: 2;
}
.views-table {
  width: 100%;
  height: 400px;
}

.apply-views {
  margin: 20px 0;
}
</style>
